import { getCookie } from './utils';
import { isDailyLimitError } from './priceEstimation';
import { LatLng } from './types';

/**
 * Address a listing's estimate is requested for. Matches the shape the publish
 * flow already builds; `coordinates` falls back to the listing location when the
 * address itself carries none.
 */
export interface EstimationAddress {
  street?: string;
  houseNumber?: string;
  city?: string;
  postcode?: string;
  country?: string;
  coordinates?: LatLng;
}

/**
 * The property facts the estimate is derived from, mirroring the backend's
 * ESTIMATION_INPUT_FIELDS. Kept as resolved values rather than form state so
 * both the publish wizard and the edit flow can call this.
 */
export interface EstimationPropertyDetails {
  description: string;
  size: number | null;
  rooms: string;
  floor: number | null;
  hasElevator: boolean;
  hasBalcony: boolean;
  renovationStatus: string;
  buildingType: string;
}

/**
 * Fire-and-forget: start the Jaakko price estimate for a sell listing, on
 * publish or after an edit that changed what the place is.
 *
 * The listing is already saved by the time this runs, so a failure here must
 * never disrupt the flow that called it. The caller gets 'started', 'daily_limit'
 * (the automated daily cap refused it) or 'failed', and carries on.
 * `automationCapability` is the one-time token the server minted for exactly
 * this listing; it is what makes the estimate the automated, listing-bound kind
 * rather than one off the user's manual quota.
 */
export const startListingPriceEstimation = async (params: {
  willingnessId: string;
  buildingId: string;
  automationCapability?: string;
  address: EstimationAddress;
  // Fallback coordinate for addresses that carry none. Optional because the
  // estimate only needs a city or street plus the country; sending a made-up
  // point would be worse than sending none.
  location?: LatLng;
  propertyDetails: EstimationPropertyDetails;
  language: string;
}): Promise<'started' | 'daily_limit' | 'failed'> => {
  const {
    willingnessId, buildingId, automationCapability, address, location, propertyDetails, language,
  } = params;
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/price-estimation/start`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': getCookie('csrf_access_token'),
      },
      body: JSON.stringify({
        address: {
          street: address.street,
          houseNumber: address.houseNumber,
          city: address.city,
          postcode: address.postcode,
          country: address.country || 'FI',
          coordinates: address.coordinates ?? location,
        },
        property_details: propertyDetails,
        building_id: buildingId,
        willingness_id: willingnessId,
        // Omitted rather than sent empty: the backend rejects the whole request
        // when the key is present but does not resolve to a live capability.
        ...(automationCapability ? { automation_capability: automationCapability } : {}),
        language,
        user_agent: navigator.userAgent,
      }),
    });
    if (response.ok) return 'started';
    const body = await response.json().catch(() => null);
    console.error(
      `Failed to start listing price estimation: HTTP ${response.status}`,
      body,
    );
    return isDailyLimitError(body) ? 'daily_limit' : 'failed';
  } catch (error) {
    console.error('Failed to start listing price estimation:', error);
    return 'failed';
  }
};

export default startListingPriceEstimation;
